import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Stack, Paper, FormControl, InputLabel, Select, MenuItem,
  Typography, Skeleton, useTheme, alpha,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  Tooltip, Button, Slide, CircularProgress, Divider, IconButton
} from '@mui/material';
import {
  BookmarkBorder, CheckCircleOutline, ReportProblemOutlined,
  EditOutlined, Save, Undo, MenuBook, CallSplitRounded
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import { teachingLoadsApi, SubjectAssignmentGridCell, CellTeacher } from '../../services/sessions/teachingLoadsApi';
import { classesApi } from '../../services/sessions/classesApi';
import { useModulePermissions } from '../../hooks/useModulePermissions';
import TeacherPickerSelect, { TeacherOption } from './TeacherPickerSelect';
import SectionChip from './SectionChip';
import EmptyState from '../common/EmptyState';
import SplitPeriodsDialog from './SplitPeriodsDialog';

interface Props {
  sessionId: string;
  teachers: TeacherOption[];
}

interface ClassOption { _id: string; name: string; }

// Sentinel for `sectionInstanceId = null` rows (class-wide cells emitted by the
// backend when the class has no SectionInstance rows). Used as the row key in
// the matrix and the pending-changes map; the API write still passes the real
// `cell.sectionInstanceId` (which is null for these rows).
const NO_SECTION_KEY = '__no_section__';

const cellKey = (sectionKey: string, subjectId: string) => `${sectionKey}::${subjectId}`;

/** Two share lists are the same assignment (same teachers, same split). */
const sameShares = (a: CellTeacher[], b: CellTeacher[]) =>
  a.length === b.length && a.every((t, i) => t._id === b[i]._id && (t.periods ?? null) === (b[i].periods ?? null) && (t.doublePeriods ?? null) === (b[i].doublePeriods ?? null));

const SubjectTeachersTab: React.FC<Props> = ({ sessionId, teachers }) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { canWrite } = useModulePermissions();
  const writable = canWrite('teaching_loads');

  const [classes, setClasses]   = useState<ClassOption[]>([]);
  const [classId, setClassId]   = useState<string>('');
  const [cells, setCells]       = useState<SubjectAssignmentGridCell[]>([]);
  const [loading, setLoading]   = useState(true);
  const [classesLoading, setClassesLoading] = useState(true);

  // Pending edits scoped to the currently-loaded class. Reset whenever the
  // class picker changes so a user can't unknowingly save edits to a class
  // they're no longer viewing.
  const [pending, setPending] = useState<Map<string, CellTeacher[]>>(new Map());
  const [saving, setSaving] = useState(false);
  const [splitting, setSplitting] = useState<SubjectAssignmentGridCell | null>(null);

  // Bootstrap — load classes once, pre-select the first one.
  useEffect(() => {
    let cancelled = false;
    setClassesLoading(true);
    (async () => {
      try {
        const list = await classesApi.list();
        if (cancelled) return;
        const safe = (list ?? []).map((c: any) => ({ _id: c._id, name: c.name }));
        setClasses(safe);
        if (safe[0] && !classId) setClassId(safe[0]._id);
      } catch (err: any) {
        enqueueSnackbar(err?.message || 'Could not load classes', { variant: 'error' });
      } finally {
        if (!cancelled) setClassesLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCells = useCallback(async () => {
    if (!classId) { setCells([]); setLoading(false); return; }
    setLoading(true);
    try {
      const data = await teachingLoadsApi.subjectAssignmentGrid(sessionId, classId);
      setCells(data);
      setPending(new Map());
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Could not load subject assignments', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [sessionId, classId, enqueueSnackbar]);

  useEffect(() => { loadCells(); }, [loadCells]);

  // Reshape the flat cell list into a (section × subject) matrix for the table.
  const matrix = useMemo(() => {
    const sectionsMap = new Map<string, { _id: string; name: string; isVirtual: boolean }>();
    const subjectsMap = new Map<string, { _id: string; name: string }>();
    for (const c of cells) {
      const sectionKey = c.sectionInstanceId ?? NO_SECTION_KEY;
      if (!sectionsMap.has(sectionKey)) {
        sectionsMap.set(sectionKey, {
          _id:       sectionKey,
          name:      c.sectionName ?? '— (no section)',
          isVirtual: c.sectionInstanceId === null
        });
      }
      if (!subjectsMap.has(c.subjectId)) {
        subjectsMap.set(c.subjectId, { _id: c.subjectId, name: c.subjectName });
      }
    }
    // Sort with the virtual "no section" row pinned to the bottom so it
    // doesn't shuffle around alphabetically with real sections.
    const sections = Array.from(sectionsMap.values()).sort((a, b) => {
      if (a.isVirtual !== b.isVirtual) return a.isVirtual ? 1 : -1;
      return a.name.localeCompare(b.name);
    });
    const subjects = Array.from(subjectsMap.values()).sort((a, b) => a.name.localeCompare(b.name));

    const cellMap = new Map<string, SubjectAssignmentGridCell>();
    for (const c of cells) {
      const sectionKey = c.sectionInstanceId ?? NO_SECTION_KEY;
      cellMap.set(cellKey(sectionKey, c.subjectId), c);
    }

    return { sections, subjects, cellMap };
  }, [cells]);

  // Resolve the value to render for a cell — pending edit wins over server state.
  const effectiveTeachersFor = useCallback(
    (cell: SubjectAssignmentGridCell): CellTeacher[] => {
      const k = cellKey(cell.sectionInstanceId ?? NO_SECTION_KEY, cell.subjectId);
      return pending.get(k) ?? cell.teachers;
    },
    [pending]
  );

  const stats = useMemo(() => {
    const total = cells.length;
    const assigned = cells.filter(c => effectiveTeachersFor(c).length > 0).length;
    return { total, assigned, unassigned: total - assigned };
  }, [cells, effectiveTeachersFor]);

  /** Stage a cell's new teachers; back to what's saved clears the edit. */
  const stage = useCallback((cell: SubjectAssignmentGridCell, next: CellTeacher[]) => {
    if (!writable) return;
    const k = cellKey(cell.sectionInstanceId ?? NO_SECTION_KEY, cell.subjectId);
    setPending(prev => {
      const map = new Map(prev);
      if (sameShares(next, cell.teachers)) map.delete(k);
      else map.set(k, next);
      return map;
    });
  }, [writable]);

  const handlePick = (cell: SubjectAssignmentGridCell, teacher: TeacherOption | null) =>
    stage(cell, teacher ? [{ _id: teacher._id, name: teacher.name, periods: null, doublePeriods: null }] : []);

  const handleDiscard = () => setPending(new Map());

  const handleSave = async () => {
    if (pending.size === 0) return;
    setSaving(true);

    const entries = Array.from(pending.entries());
    const cellsByKey = new Map(
      cells.map(c => [cellKey(c.sectionInstanceId ?? NO_SECTION_KEY, c.subjectId), c])
    );

    const results = await Promise.allSettled(
      entries.map(async ([key, shares]) => {
        const cell = cellsByKey.get(key);
        if (!cell) throw new Error(`Cell no longer present: ${key}`);
        const saved = await teachingLoadsApi.setSubjectTeachers({
          sessionId,
          classId:           cell.classId,
          sectionInstanceId: cell.sectionInstanceId,
          subjectId:         cell.subjectId,
          teachers:          shares.map(t => ({ teacherId: t._id, periods: t.periods, doublePeriods: t.doublePeriods }))
        });
        return { key, saved };
      })
    );

    const succeededKeys = new Set<string>();
    const succeeded: Array<{ key: string; saved: CellTeacher[] }> = [];
    const failures: string[] = [];
    results.forEach((r) => {
      if (r.status === 'fulfilled') {
        succeededKeys.add(r.value.key);
        succeeded.push(r.value);
      } else {
        failures.push(r.reason?.message || 'Could not save');
      }
    });
    const failedCount = failures.length;

    if (succeeded.length > 0) {
      setCells(prev => prev.map(c => {
        const k = cellKey(c.sectionInstanceId ?? NO_SECTION_KEY, c.subjectId);
        const hit = succeeded.find(s => s.key === k);
        return hit ? { ...c, teachers: hit.saved } : c;
      }));
    }

    setPending(prev => {
      const next = new Map(prev);
      for (const k of Array.from(succeededKeys)) next.delete(k);
      return next;
    });

    setSaving(false);

    if (failedCount === 0) {
      enqueueSnackbar(
        succeeded.length === 1
          ? 'Subject teacher updated'
          : `${succeeded.length} subject teacher assignments updated`,
        { variant: 'success' }
      );
    } else if (succeeded.length === 0) {
      enqueueSnackbar(
        failedCount === 1
          ? failures[0]
          : `Could not save ${failedCount} changes: ${failures[0]}`,
        { variant: 'error' }
      );
    } else {
      enqueueSnackbar(
        `Saved ${succeeded.length}, ${failedCount} failed — failed cells are kept for retry`,
        { variant: 'warning' }
      );
    }
  };

  const pendingCount = pending.size;

  if (classesLoading) {
    return (
      <Box>
        <Skeleton variant="rounded" height={56} sx={{ mb: 2 }} />
        <Skeleton variant="rounded" height={280} />
      </Box>
    );
  }

  if (classes.length === 0) {
    return (
      <EmptyState
        icon={<BookmarkBorder sx={{ fontSize: 48 }} />}
        title="No classes set up yet"
        description="Add classes from Settings → Classes before assigning subject teachers."
      />
    );
  }

  return (
    <Box sx={{ position: 'relative', pb: pendingCount > 0 ? 8 : 0 }}>
      {/* Top bar: class picker + stats */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Class</InputLabel>
            <Select
              value={classId}
              label="Class"
              onChange={(e) => {
                if (pending.size > 0) {
                  const ok = window.confirm(
                    `You have ${pending.size} unsaved change${pending.size === 1 ? '' : 's'} in this class. Switching classes will discard them. Continue?`
                  );
                  if (!ok) return;
                }
                setClassId(e.target.value);
              }}
            >
              {classes.map(c => (
                <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ flex: 1 }} />

          <Stack direction="row" spacing={2} alignItems="center">
            <CellStat
              icon={<BookmarkBorder color="primary" fontSize="small" />}
              label="Cells"
              value={stats.total}
            />
            <CellStat
              icon={<CheckCircleOutline color="success" fontSize="small" />}
              label="Assigned"
              value={stats.assigned}
              accent="success.main"
            />
            <CellStat
              icon={<ReportProblemOutlined color="warning" fontSize="small" />}
              label="Unassigned"
              value={stats.unassigned}
              accent={stats.unassigned > 0 ? 'warning.main' : undefined}
            />
            {pendingCount > 0 && (
              <>
                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <EditOutlined sx={{ color: 'warning.main', fontSize: 18 }} />
                  <Typography variant="caption" sx={{ color: 'warning.dark', fontWeight: 600 }}>
                    {pendingCount} unsaved
                  </Typography>
                </Stack>
              </>
            )}
          </Stack>
        </Stack>
      </Paper>

      {/* Grid */}
      <Paper elevation={0} sx={{ borderRadius: 2, border: `1px solid ${theme.palette.divider}`, overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ p: 2 }}>
            <Skeleton variant="rounded" height={56} sx={{ mb: 1 }} />
            <Skeleton variant="rounded" height={56} sx={{ mb: 1 }} />
            <Skeleton variant="rounded" height={56} />
          </Box>
        ) : cells.length === 0 ? (
          <Box sx={{ py: 6 }}>
            <EmptyState
              icon={<BookmarkBorder sx={{ fontSize: 36 }} />}
              title="No curriculum in this class"
              description="Add subjects to the class curriculum in the Curriculum tab — then assign teachers here."
            />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      color: 'text.secondary',
                      // Opaque sticky background — paper underneath, subtle
                      // tint on top — matches the rest of the header visually
                      // without letting subject columns bleed through on scroll.
                      bgcolor: 'background.paper',
                      backgroundImage: `linear-gradient(${alpha(theme.palette.text.primary, 0.025)}, ${alpha(theme.palette.text.primary, 0.025)})`,
                      position: 'sticky',
                      left: 0,
                      // Higher than both other header cells (zIndex 2 by MUI
                      // stickyHeader default) and the body sticky cells so it
                      // sits above everything during horizontal scroll.
                      zIndex: 4,
                      borderRight: `1px solid ${theme.palette.divider}`,
                      minWidth: 160,
                      whiteSpace: 'nowrap'
                    }}
                  >
                    Section
                  </TableCell>
                  {matrix.subjects.map(s => (
                    <TableCell
                      key={s._id}
                      sx={{
                        bgcolor: alpha(theme.palette.text.primary, 0.025),
                        // Room for the picker plus the split button beside it.
                        minWidth: 252,
                        py: 1
                      }}
                    >
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.75,
                          px: 1.25,
                          py: 0.5,
                          borderRadius: 999,
                          bgcolor: 'background.paper',
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
                          color: 'primary.dark',
                          maxWidth: '100%',
                          boxShadow: `0 1px 2px ${alpha(theme.palette.common.black, 0.04)}`
                        }}
                      >
                        <MenuBook sx={{ fontSize: 14, color: 'primary.main', flexShrink: 0 }} />
                        <Typography
                          variant="caption"
                          noWrap
                          sx={{ fontWeight: 600, letterSpacing: 0.15, fontSize: '0.75rem' }}
                        >
                          {s.name}
                        </Typography>
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {matrix.sections.map(section => (
                  <TableRow key={section._id}>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        bgcolor: 'background.paper',
                        position: 'sticky',
                        left: 0,
                        zIndex: 1,
                        borderRight: `1px solid ${theme.palette.divider}`,
                        minWidth: 160
                      }}
                    >
                      {section.isVirtual ? (
                        <SectionChip
                          name={section.name}
                          isVirtual
                          tooltip="This class has no sections — assignments here apply class-wide."
                        />
                      ) : (
                        <SectionChip name={section.name} />
                      )}
                    </TableCell>
                    {matrix.subjects.map(subject => {
                      const cell = matrix.cellMap.get(cellKey(section._id, subject._id));
                      if (!cell) {
                        // Subject not in this section's curriculum (rare for class-wide
                        // curricula; common when a section overrides). Render a quiet dash.
                        return (
                          <TableCell key={subject._id} sx={{ color: 'text.disabled' }}>
                            <Tooltip title={`${subject.name} is not in this section's curriculum`}>
                              <Box component="span">—</Box>
                            </Tooltip>
                          </TableCell>
                        );
                      }
                      const k = cellKey(section._id, subject._id);
                      const isPending = pending.has(k);
                      const effective = effectiveTeachersFor(cell);
                      const canSplit = writable && !saving && cell.curriculum.periodsPerWeek > 1;
                      return (
                        <TableCell
                          key={subject._id}
                          sx={{
                            verticalAlign: 'middle',
                            '&:hover .split-btn, &:focus-within .split-btn': { opacity: 1 },
                            ...(isPending && {
                              bgcolor: alpha(theme.palette.warning.main, 0.04)
                            })
                          }}
                        >
                          {effective.length > 1 ? (
                            <SplitSummary teachers={effective} pending={isPending} disabled={!canSplit} onOpen={() => setSplitting(cell)} />
                          ) : (
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <TeacherPickerSelect
                                  teachers={teachers}
                                  value={effective[0] ?? null}
                                  onChange={(t) => handlePick(cell, t)}
                                  disabled={!writable || saving}
                                  pending={isPending}
                                />
                              </Box>
                              {canSplit && (
                                <Tooltip title="Split periods between teachers">
                                  <IconButton
                                    className="split-btn"
                                    size="small"
                                    onClick={() => setSplitting(cell)}
                                    aria-label={`Split ${subject.name} between teachers`}
                                    sx={{ opacity: { xs: 1, md: 0 }, transition: 'opacity 120ms', color: 'text.secondary' }}
                                  >
                                    <CallSplitRounded fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Stack>
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <SplitPeriodsDialog
        open={!!splitting}
        cell={splitting}
        initial={splitting ? effectiveTeachersFor(splitting) : []}
        teachers={teachers}
        onClose={() => setSplitting(null)}
        onApply={(next) => { if (splitting) stage(splitting, next); setSplitting(null); }}
      />

      {/* Sticky save bar — slides up once the page has unsaved edits. */}
      <Slide in={pendingCount > 0} direction="up" mountOnEnter unmountOnExit>
        <Paper
          elevation={6}
          sx={{
            position: 'sticky',
            bottom: 16,
            mt: 2,
            mx: 'auto',
            maxWidth: 640,
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.warning.main, 0.4)}`,
            bgcolor: 'background.paper',
            px: 2,
            py: 1.25,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            boxShadow: `0 10px 30px ${alpha(theme.palette.common.black, 0.12)}`
          }}
        >
          <Box
            sx={{
              width: 32, height: 32, borderRadius: '50%',
              bgcolor: alpha(theme.palette.warning.main, 0.12),
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}
          >
            <EditOutlined sx={{ fontSize: 18, color: 'warning.dark' }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={600}>
              {pendingCount} unsaved change{pendingCount === 1 ? '' : 's'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Edits are kept locally — click Save to apply.
            </Typography>
          </Box>
          <Button
            size="small"
            variant="text"
            color="inherit"
            startIcon={<Undo fontSize="small" />}
            onClick={handleDiscard}
            disabled={saving}
            sx={{ textTransform: 'none' }}
          >
            Discard
          </Button>
          <Button
            size="small"
            variant="contained"
            color="primary"
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <Save fontSize="small" />}
            onClick={handleSave}
            disabled={saving}
            sx={{ textTransform: 'none', borderRadius: 2, px: 2 }}
          >
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </Paper>
      </Slide>
    </Box>
  );
};

/** A subject split between teachers: each teacher and their share, opening the split to edit. */
const SplitSummary: React.FC<{ teachers: CellTeacher[]; pending: boolean; disabled: boolean; onOpen: () => void }> = ({ teachers, pending, disabled, onOpen }) => (
  <Box
    role="button"
    tabIndex={disabled ? -1 : 0}
    onClick={disabled ? undefined : onOpen}
    onKeyDown={(e) => { if (!disabled && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onOpen(); } }}
    sx={(theme) => ({
      display: 'flex', alignItems: 'center', gap: 1, px: 1.25, py: 0.75, borderRadius: '8px', outline: 'none',
      border: '1px solid', borderColor: pending ? alpha(theme.palette.warning.main, 0.6) : 'divider',
      cursor: disabled ? 'default' : 'pointer',
      '&:hover': disabled ? undefined : { borderColor: 'text.disabled' },
      '&:focus-visible': { boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.25)}` },
    })}
  >
    <CallSplitRounded sx={{ fontSize: 16, color: 'primary.main', flexShrink: 0 }} />
    <Box sx={{ flex: 1, minWidth: 0 }}>
      {teachers.map(t => (
        <Typography key={t._id} variant="body2" noWrap sx={{ lineHeight: 1.45 }}>
          {t.name} <Box component="span" sx={{ fontWeight: 700 }}>· {t.periods}</Box>
          {!!t.doublePeriods && (
            <Box component="span" sx={{ color: 'text.secondary', fontSize: '0.72rem' }}> ({t.doublePeriods} double{t.doublePeriods === 1 ? '' : 's'})</Box>
          )}
        </Typography>
      ))}
    </Box>
  </Box>
);

const CellStat: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: number;
  accent?: string;
}> = ({ icon, label, value, accent }) => (
  <Stack direction="row" spacing={0.75} alignItems="center">
    {icon}
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1 }}>{label}</Typography>
      <Typography variant="subtitle1" fontWeight={700} sx={{ color: accent ?? 'text.primary', lineHeight: 1.2 }}>
        {value}
      </Typography>
    </Box>
  </Stack>
);

export default SubjectTeachersTab;
